/**
 * doc.edit engine. The chat model decides WHAT to change (a self-contained instruction plus
 * optional target block ids); this module decides HOW: it snapshots the editor's blocks and
 * selection, grounds the request in the project's requirements and meeting notes, asks the
 * block patch planner (`aiService.editDocument` with `blocks`: task 'doc.patch', escalating
 * once to 'doc.patch.escalate', JSON schema, stable seed, image/diagram aliasing) for
 * block-anchored ops, re-validates them against the live blocks, and applies them through
 * the editor bridge with review:true so every change lands as a suggestion the user accepts
 * or rejects in the editor.
 *
 * Refinement is cumulative: while the suggestions of the previous edit are still pending,
 * a follow-up ("make it shorter") re-plans with the whole instruction chain and the previous
 * operations, so the new suggestion builds on the one on screen instead of re-rolling it. The
 * pending suggestions stay on screen while Inky plans (the bridge plans against the pre-review
 * baseline); the new review replaces them in one step only once the new plan has landed, so a
 * failed refinement leaves the user's suggestions where they were.
 *
 * Every await can outlive the document it started on (the user may open another one while Inky
 * thinks), so the open editor is checked against the snapshot before anything is applied.
 *
 * An empty document is drafted in one grounded call (task 'doc.rewrite'). Editors without
 * the bridge (no getBlocks/applyEditPlan) use the HTML patch planner and the older
 * showPatchDiffEdits/showDiffEdits review.
 */

import aiService from "../services/aiService";
import { stableSeed } from "../constants/aiModels";
import { convertMermaidBlocks } from "../utils/mermaidUtils";
import { pageBreakPromptRule } from "../utils/pageBreakHtml";
import { buildProjectGrounding } from "./projectGrounding";
import { DRAFT_SCHEMA, describeTargets, validateEditOps } from "./docEditOps";
import { existingImageSources, imagePlacementMap, prepareHtmlForEditor, prepareOpsForEditor, restoreImagePlacement, sanitizeEditHtml } from "./editHtml";
import {
  getEditorHandle,
  readEditorHtml,
  rejectAllSuggestions,
  snapshotEditor,
  supportsEditPlans,
} from "./editorBridge";

/** Instructions kept in a refinement chain. */
const MAX_CHAIN = 6;

/**
 * Per-document edit chains for cumulative refinement. Keyed by document/template id.
 * @type {Map<string, {instructions: string[], operations: Array<object>, reviewId: string|null}>}
 */
const editChains = new Map();

/** Review ids withdrawn by a refinement (their cards read "Refined", not "Undone"). */
const withdrawnReviews = new Set();

/** Ops kept on a card (for "Show again") while their JSON fits this many characters. */
const CARD_OPS_LIMIT = 30000;

const DRAFT_RULES = `You draft documents for Quill, a PM workspace. Return JSON with a short summary and the full document body as HTML.
Rules:
- Use clean semantic HTML (h1-h3, p, ul/ol/li, table, strong, em). No markdown, no code fences, no inline styles, no <html>/<body>.
- Ground every statement in the instruction and the PROJECT EVIDENCE. Never invent requirements, stakeholders, dates, budgets or metrics; where something is unknown write [Needs input: what is missing].
- Keep requirement titles verbatim and cite evidence like (R2) or (S1) where it is used.
- Structure the document the way a senior product/project manager would for the requested document type; be specific and concise.
- Do not write page headers, footers or page numbers into the body: they are set in Page setup, outside the document text.
- ${pageBreakPromptRule()}`;

const TEMPLATE_DRAFT_RULES = `${DRAFT_RULES}
- This is a reusable TEMPLATE: write the fixed structure and boilerplate, and mark content the generator must fill as <span data-tpl-field data-kind="text">instruction for the AI</span> (a div for longer sections). Use {{project.name}}, {{customer.name}} and {{doc.date}} variables where appropriate. Do not put project-specific facts in a template.`;

const EDIT_RULES = `RULES FOR THIS EDIT:
- Use ONLY facts from the instruction, the document and the PROJECT EVIDENCE. Never invent requirements, names, dates or numbers; where a fact is missing write [Needs input: what is missing].
- Keep requirement titles and quoted meeting text verbatim; you may cite them like (R3) or (S1).
- Keep every data-tpl-field / data-field-id element with its attributes, and every {{variable}}, exactly as written.
- Write clean semantic HTML (p, h1-h4, ul/ol/li, table, strong, em, a); no markdown, no inline styles.
- Keep every <img> tag with all its attributes (src, alt, width, height, data-align, data-wrap) exactly as written unless the instruction asks to move, wrap, resize or remove that image. To stop text wrapping around an image write data-wrap="none"; to centre it write data-align="center" (a centred image is never wrapped); to give it back its own size write width="auto" height="auto".
- Page headers, footers and page numbers are set in Page setup, not in the body: never add a header, footer or page number line to the document text.
- A block of type pageBreak is a page break. Never replace or delete it unless the user asks to remove that page break; rewrite the blocks around it instead.
- ${pageBreakPromptRule()}`;

/** What the model is told when an edit or draft hit the output limit (MAX_TOKENS). */
export const EDIT_TOO_LARGE =
  "This edit is too large to make in one pass. Try a narrower edit: one section at a time, or select the part to change.";

/**
 * Tool outcome for an edit that hit the output limit.
 * @returns {{ok: false, summary: string, data: object}} Outcome.
 */
const tooLargeOutcome = () => ({
  ok: false,
  summary: "Edit too large",
  data: { error: EDIT_TOO_LARGE, hint: "Tell the user the edit was too large and offer to do it section by section." },
});

/**
 * Key for the edit chain of the open surface.
 * @param {object} meta - Editor meta.
 * @returns {string} Chain key.
 */
const chainKey = (meta) => meta?.documentId || meta?.templateId || meta?.title || "editor";

/**
 * Parse the JSON body of a task result (json when the service parsed it, else text).
 * @param {{text?: string, json?: object}} result - Task result.
 * @returns {object|null} Parsed object.
 */
const parseJsonResult = (result) => {
  if (result?.json && typeof result.json === "object") return result.json;
  const text = String(result?.text || "").trim().replace(/^```(?:json)?\s*/i, "").replace(/```\s*$/i, "");
  const start = text.indexOf("{");
  const end = text.lastIndexOf("}");
  if (start === -1 || end <= start) return null;
  try {
    return JSON.parse(text.slice(start, end + 1));
  } catch {
    return null;
  }
};

/**
 * Strip wrappers a model sometimes adds around HTML.
 * @param {string} html - Model HTML.
 * @returns {string} Clean HTML.
 */
const cleanHtml = (html) =>
  convertMermaidBlocks(
    String(html || "")
      .trim()
      .replace(/^```(?:html)?\s*/i, "")
      .replace(/```\s*$/i, "")
      .replace(/^<html[^>]*>|<\/html>$/gi, "")
      .replace(/^<body[^>]*>|<\/body>$/gi, "")
      .trim()
  );

/**
 * Operations as compact text for a refinement prompt (inline images elided).
 * @param {Array<object>} operations - Previous ops.
 * @returns {string} JSON text.
 */
const describeOperations = (operations) =>
  JSON.stringify(operations || [])
    .replace(/src=\\?"data:[^"\\]+\\?"/g, 'src="[image]"')
    .slice(0, 12000);

/**
 * Compose the instruction section, including the refinement chain.
 * @param {string} instruction - Latest instruction.
 * @param {{instructions: string[], operations: Array<object>, applied?: boolean}|null} chain - Refinement chain.
 * @returns {string} Prompt section.
 */
const instructionSection = (instruction, chain) => {
  if (!chain?.instructions?.length) return instruction;
  const previous = chain.instructions.map((text, index) => `${index + 1}. ${text}`).join("\n");
  if (chain.applied) {
    return `${instruction}\n\nEARLIER EDITS (already accepted by the user and reflected in the document; context only):\n${previous}`;
  }
  const ops = chain.operations?.length
    ? `\nPREVIOUS OPERATIONS (still shown as suggestions; your plan replaces them):\n${describeOperations(chain.operations)}`
    : "";
  return `REFINEMENT of an edit the user was reviewing. Produce ONE complete plan that satisfies all instructions together; the latest takes precedence where they conflict. Include everything that should still change.\nPREVIOUS INSTRUCTIONS:\n${previous}${ops}\nLATEST INSTRUCTION:\n${instruction}`;
};

/**
 * Full instruction for the patch planner: the request, focus, selection, evidence and rules.
 * @param {object} params - Inputs.
 * @returns {string} Planner instruction.
 */
const composePlannerInstruction = ({ instruction, chain, grounding, snapshot, targets }) =>
  [
    instructionSection(instruction, chain),
    targets.length ? `FOCUS: change only blocks ${targets.join(", ")} unless the instruction clearly needs more.` : "",
    snapshot.selection?.text
      ? `USER SELECTION${snapshot.selection.blockIds?.length ? ` (blocks ${snapshot.selection.blockIds.join(", ")})` : ""}: "${snapshot.selection.text.slice(0, 4000)}". "This", "it" or "the selection" means this text.`
      : "",
    snapshot.meta?.kind === "template" ? "This is a reusable TEMPLATE; keep it free of project-specific facts." : "",
    grounding?.text ? `PROJECT EVIDENCE:\n${grounding.text}` : "",
    EDIT_RULES,
  ]
    .filter(Boolean)
    .join("\n\n");

/**
 * Draft an empty document or template in one grounded call. Malformed JSON is retried once.
 * @param {object} params - Draft inputs.
 * @returns {Promise<{html: string, summary: string}>} Draft.
 */
const draftHtml = async ({ instruction, chain, grounding, meta, seed, signal }) => {
  const kind = meta?.kind === "template" ? "template" : "document";
  const prompt = [
    `${kind.toUpperCase()} TITLE: ${meta?.title || "Untitled"}`,
    grounding?.text
      ? `PROJECT EVIDENCE (use only these facts):\n${grounding.text}`
      : "PROJECT EVIDENCE: none available. Do not invent project facts; use [Needs input: ...] markers.",
    `The ${kind} is empty.`,
    `INSTRUCTION:\n${instructionSection(instruction, chain)}`,
  ].join("\n\n");
  let json = null;
  for (let attempt = 0; attempt < 2 && !json?.html; attempt += 1) {
    try {
      json = parseJsonResult(
        await aiService.runTask({
          task: "doc.rewrite",
          profile: "fill",
          systemInstruction: kind === "template" ? TEMPLATE_DRAFT_RULES : DRAFT_RULES,
          prompt: attempt ? `${prompt}\n\nReturn only the JSON object with "summary" and "html".` : prompt,
          responseSchema: DRAFT_SCHEMA,
          generationConfig: { maxOutputTokens: 16384 },
          seed: attempt ? seed + 1 : seed,
          signal,
        })
      );
    } catch (error) {
      if (error?.code !== "INVALID_JSON") throw error;
    }
  }
  const html = cleanHtml(json?.html || "");
  if (!html) throw new Error("The draft came back empty. Try again with a little more detail.");
  return { html, summary: String(json?.summary || "").trim() };
};

/**
 * Remember the chain after an applied edit.
 * @param {string} key - Chain key.
 * @param {object|null} chain - Previous chain.
 * @param {string} instruction - Latest instruction.
 * @param {Array<object>} operations - Applied ops.
 * @param {string|null} reviewId - Review id.
 * @returns {void}
 */
const rememberChain = (key, chain, instruction, operations, reviewId) => {
  editChains.set(key, {
    instructions: [...(chain?.instructions || []), instruction].slice(-MAX_CHAIN),
    operations,
    reviewId,
  });
};

/**
 * Why the open editor is no longer the one a snapshot was taken from, or null when it still is.
 * @param {object} snapshot - Snapshot from snapshotEditor.
 * @param {object|null} live - The editor handle open now.
 * @returns {string|null} Reason, or null when it is the same document.
 */
const surfaceChangedReason = (snapshot, live) => {
  if (!live) return "The editor was closed before the edit could be applied.";
  let meta = null;
  if (typeof live.getMeta === "function") {
    try {
      meta = live.getMeta();
    } catch {
      meta = null;
    }
  }
  const moved = meta
    ? (snapshot.meta?.documentId ?? null) !== (meta.documentId ?? null) || (snapshot.meta?.templateId ?? null) !== (meta.templateId ?? null)
    : live !== snapshot.handle;
  return moved ? "A different document is open now, so the edit was not applied." : null;
};

/**
 * Tool outcome for an edit whose document is gone or replaced.
 * @param {string} reason - From {@link surfaceChangedReason}.
 * @returns {{ok: false, summary: string, data: object}} Outcome.
 */
const surfaceChangedOutcome = (reason) => ({
  ok: false,
  summary: reason.startsWith("A different") ? "Document changed" : "Editor closed",
  data: { error: `${reason} Tell the user, and offer to make the edit again once the document is open.` },
});

/**
 * Ops for a card, when they are small enough to keep (lets the card show them again).
 * @param {Array<object>} ops - Ops.
 * @returns {{ops?: Array<object>}} Card fields.
 */
const cardOps = (ops) => {
  try {
    return ops?.length && JSON.stringify(ops).length <= CARD_OPS_LIMIT ? { ops } : {};
  } catch {
    return {};
  }
};

/**
 * Live blocks of an editor (empty when it has no getBlocks).
 * @param {object} handle - Editor handle.
 * @returns {Array<object>} Blocks.
 */
const liveBlocksOf = (handle) => {
  try {
    return typeof handle?.getBlocks === "function" ? handle.getBlocks() || [] : [];
  } catch {
    return [];
  }
};

/**
 * Edit (or draft) the open document/template from a natural-language instruction.
 * @param {object} params - Edit inputs.
 * @param {() => object|null} params.getEditorRef - ActiveEditorContext getter.
 * @param {string} params.instruction - Self-contained instruction from the chat model.
 * @param {string[]} [params.targetBlockIds] - Blocks the user referred to.
 * @param {boolean} [params.refine] - Model's hint that this adjusts the previous edit.
 * @param {object|null} [params.project] - Project for grounding.
 * @param {object} [params.fallbackMeta] - Route-derived meta for editors without getMeta.
 * @param {number} [params.variant] - Regenerate counter (changes the seed on purpose).
 * @param {string} [params.accountId] - Account for tier resolution.
 * @param {AbortSignal} [params.signal] - Abort signal.
 * @param {(label: string) => void} [params.onProgress] - Step label updates.
 * @returns {Promise<{ok: boolean, summary: string, data: object, card?: object}>} Tool outcome.
 */
export const runDocEdit = async ({
  getEditorRef,
  instruction,
  targetBlockIds = [],
  refine = false,
  project = null,
  fallbackMeta = null,
  variant = 0,
  accountId,
  signal,
  onProgress,
}) => {
  let snapshot = snapshotEditor(getEditorRef, { fallbackMeta });
  if (!snapshot) {
    return { ok: false, summary: "No editor is open", data: { error: "No document or template is open. Ask the user to open one first." } };
  }
  if (snapshot.meta?.readOnly) {
    return { ok: false, summary: "The editor is read-only", data: { error: "The open editor is read-only (preview mode). Ask the user to switch to editing." } };
  }

  const key = chainKey(snapshot.meta);
  const previousChain = editChains.get(key) || null;
  // Cumulative refinement: an edit while our previous suggestions are still pending builds on
  // them (re-planned together; the new review supersedes the pending one only once it lands).
  // When the model says it is refining but the user already resolved the review, earlier
  // instructions are context only.
  // Only refine reviews Inky itself opened; any other open review (e.g. "Regenerate section") is
  // left alone so the editor's review_pending guard can tell the user to resolve it first.
  const pendingLabel = snapshot.pendingReview?.origin || snapshot.pendingReview?.label || "";
  const pendingIsInky = !pendingLabel || /^Inky's /.test(pendingLabel) || pendingLabel === "inky";
  const refining = Boolean(previousChain && snapshot.pendingReview && pendingIsInky);
  let supersededReviewId = refining ? snapshot.pendingReview.reviewId || previousChain.reviewId || null : null;
  if (refining && !supportsEditPlans(snapshot.handle)) {
    // An editor without the bridge cannot plan against the pre-review baseline, so its pending
    // diff has to go before the html is read (the behaviour such editors always had).
    if (supersededReviewId) withdrawnReviews.add(supersededReviewId);
    supersededReviewId = null;
    rejectAllSuggestions(snapshot.handle);
    snapshot = snapshotEditor(getEditorRef, { fallbackMeta }) || snapshot;
  }
  /**
   * Record that the pending review was replaced by a refinement (its card reads "Refined below").
   * @param {string|null} newReviewId - The review that replaced it.
   * @returns {void}
   */
  const markSuperseded = (newReviewId) => {
    if (supersededReviewId && newReviewId && newReviewId !== supersededReviewId) withdrawnReviews.add(supersededReviewId);
  };
  const chain = refining ? previousChain : refine && previousChain ? { ...previousChain, applied: true } : null;
  const targets = (targetBlockIds || []).filter((id) => snapshot.blocks.some((block) => block.id === id));

  let grounding = null;
  if (project && snapshot.meta?.kind !== "template") {
    onProgress?.(`Reading requirements for ${project.name}`);
    grounding = await buildProjectGrounding({ project, query: instruction });
    const moved = surfaceChangedReason(snapshot, getEditorHandle(getEditorRef));
    if (moved) return surfaceChangedOutcome(moved);
  }

  const label = snapshot.meta?.kind === "template" ? "Inky's template edit" : "Inky's edit";
  const documentId = snapshot.meta?.documentId || snapshot.meta?.templateId || key;
  const baseCard = {
    kind: "doc_edit",
    status: "pending_review",
    label,
    documentId: snapshot.meta?.documentId || null,
    templateId: snapshot.meta?.templateId || null,
    grounding: grounding?.counts || null,
  };

  // Empty surface: draft the whole body.
  if (snapshot.isEmpty) {
    onProgress?.(`Drafting ${snapshot.meta?.title ? `"${snapshot.meta.title}"` : "the document"}`);
    const seed = stableSeed("doc.draft", documentId, ...(chain?.instructions || []), instruction, variant);
    let draft;
    try {
      draft = await draftHtml({ instruction, chain, grounding, meta: snapshot.meta, seed, signal });
    } catch (error) {
      if (error?.code === "MAX_TOKENS") return tooLargeOutcome();
      throw error;
    }
    const live = getEditorHandle(getEditorRef);
    const moved = surfaceChangedReason(snapshot, live);
    if (moved) return surfaceChangedOutcome(moved);
    // Nothing that loads or runs on display reaches the editor (see editHtml.js).
    const html = prepareHtmlForEditor(draft.html, liveBlocksOf(live)).html;
    if (!html.trim()) {
      return { ok: false, summary: "Draft not applied", data: { error: "The draft had no usable content. Try again with a little more detail." } };
    }
    let reviewId = null;
    let reviewable = true;
    if (typeof live.setContentWithReview === "function") {
      const outcome = live.setContentWithReview(html, { label }) || {};
      if (outcome.refused) {
        return {
          ok: false,
          summary: "The editor refused the draft",
          data: { error: `The editor refused the draft (${outcome.refused}). Tell the user why and suggest a narrower request.` },
        };
      }
      reviewId = outcome.reviewId || null;
    } else if (supportsEditPlans(live) && snapshot.blocks.length) {
      reviewId = live.applyEditPlan([{ op: "replace_block", blockId: snapshot.blocks[0].id, html }], { review: true, label })?.reviewId || null;
    } else {
      live.setContent?.(html);
      reviewable = false;
    }
    markSuperseded(reviewId);
    rememberChain(key, chain, instruction, [], reviewId);
    const summary = draft.summary || "Drafted the document.";
    return {
      ok: true,
      summary: "Drafted the document",
      data: { ok: true, mode: "draft", summary, grounding: grounding?.counts || null, note: reviewable ? "The draft is shown in the editor for review; nothing is saved until the user accepts it." : "The draft was placed in the editor; the user can edit it before saving." },
      card: { ...baseCard, mode: "draft", status: reviewable ? "pending_review" : "accepted", summary, changeCount: 1, failedCount: 0, reviewId, ...(reviewable ? cardOps([{ op: "replace_document", html }]) : {}) },
    };
  }

  const bridged = supportsEditPlans(snapshot.handle);
  onProgress?.(describeTargets(snapshot.blocks, targets) || (refining ? "Refining the edit" : "Planning the edit"));
  const plannerInstruction = composePlannerInstruction({ instruction, chain, grounding, snapshot, targets });
  const currentHtml = bridged ? "" : readEditorHtml(snapshot.handle);

  let result;
  try {
    result = await aiService.editDocument(currentHtml, plannerInstruction, {
      ...(bridged ? { blocks: snapshot.blocks, allowRewrite: false } : {}),
      documentId,
      variant,
      accountId,
      signal,
    });
  } catch (error) {
    if (error?.code === "MAX_TOKENS") return tooLargeOutcome();
    if (error?.code === "PATCH_FAILED") {
      return {
        ok: false,
        summary: "Could not plan the edit",
        data: { error: `${error.message} Ask the user to point at the section (select it), or rephrase the request.` },
      };
    }
    throw error;
  }

  const live = getEditorHandle(getEditorRef);
  const moved = surfaceChangedReason(snapshot, live);
  if (moved) return surfaceChangedOutcome(moved);
  const summary = String(result?.thinking || "").trim() || "Suggested changes to the document.";

  if (bridged) {
    if (!supportsEditPlans(live)) return { ok: false, summary: "Editor changed", data: { error: "The editor changed before the edit could be applied. Try again." } };
    onProgress?.("Applying suggestions");
    // Re-validate against the live blocks: the user may have typed while the plan was made.
    const liveBlocks = typeof live.getBlocks === "function" ? liveBlocksOf(live) : snapshot.blocks;
    const checked = validateEditOps(result?.editPlan || [], liveBlocks);
    const { errors } = checked;
    // Nothing that loads or runs on display reaches the editor (see editHtml.js).
    const valid = prepareOpsForEditor(checked.valid, liveBlocks).ops;
    if (!valid.length) {
      return {
        ok: false,
        summary: "Could not place the edit",
        data: { error: `The planned changes no longer match the document: ${errors.slice(0, 3).join(" ")}` },
      };
    }
    const outcome = live.applyEditPlan(valid, { review: true, label }) || {};
    const applied = Array.isArray(outcome.applied) ? outcome.applied : [];
    const failed = [...(Array.isArray(outcome.failed) ? outcome.failed : []), ...errors.map((reason) => ({ reason }))];
    if (!applied.length) {
      const reasons = failed.map((item) => item?.reason).filter(Boolean);
      return {
        ok: false,
        summary: "The editor refused the edit",
        data: { error: `The editor refused every change${reasons.length ? `: ${reasons.slice(0, 3).join("; ")}` : ""}. Tell the user why and suggest a narrower request.` },
      };
    }
    markSuperseded(outcome.reviewId || null);
    rememberChain(key, chain, instruction, valid, outcome.reviewId || null);
    const firstBlockId = applied.map((item) => item?.op?.blockId || item?.blockId).find(Boolean) || null;
    return {
      ok: true,
      summary: `Suggested ${applied.length} change${applied.length === 1 ? "" : "s"}`,
      data: {
        ok: true,
        mode: refining ? "refine" : "patch",
        summary,
        changesSuggested: applied.length,
        ...(failed.length ? { notApplied: failed.map((item) => item?.reason).filter(Boolean).slice(0, 5) } : {}),
        grounding: grounding?.counts || null,
        note: "Suggestions are highlighted in the editor; nothing is saved until the user accepts them.",
      },
      card: { ...baseCard, mode: refining ? "refine" : "patch", summary, changeCount: applied.length, failedCount: failed.length, reviewId: outcome.reviewId || null, firstBlockId, ...cardOps(valid) },
    };
  }

  // Editor without the bridge: the older diff review over HTML.
  onProgress?.("Applying suggestions");
  const safety = { existingSources: existingImageSources([{ html: currentHtml }]) };
  const placement = imagePlacementMap([{ html: currentHtml }]);
  const safeHtml = (value) => sanitizeEditHtml(restoreImagePlacement(value, placement).html, safety).html;
  let diff = null;
  if (result?.editStrategy === "patch" && Array.isArray(result.operations) && typeof live.showPatchDiffEdits === "function") {
    const operations = result.operations.map((operation) =>
      typeof operation?.replace === "string" ? { ...operation, replace: safeHtml(operation.replace) } : operation
    );
    diff = live.showPatchDiffEdits(currentHtml, operations);
    if (!diff?.hasChanges || diff?.failedOperations?.length) diff = null;
  }
  if (!diff && result?.html && typeof live.showDiffEdits === "function") {
    diff = live.showDiffEdits(currentHtml, safeHtml(convertMermaidBlocks(result.html)));
  }
  const hasChanges = typeof diff === "boolean" ? diff : Boolean(diff?.hasChanges);
  if (!hasChanges) {
    return { ok: true, summary: "No changes needed", data: { ok: true, changesSuggested: 0, note: "The document already matches the request." } };
  }
  rememberChain(key, chain, instruction, result.operations || [], null);
  return {
    ok: true,
    summary: "Suggested changes",
    data: { ok: true, mode: "rewrite", summary, note: "Changes are highlighted in the editor; nothing is saved until the user accepts them." },
    card: { ...baseCard, mode: "rewrite", summary, changeCount: diff?.changes?.length || 1, failedCount: 0, reviewId: null },
  };
};

/**
 * Forget the refinement chain of a surface (its review was accepted or undone).
 * @param {object} meta - Editor meta (documentId/templateId).
 * @returns {void}
 */
export const resetEditChain = (meta) => {
  editChains.delete(chainKey(meta));
};

/**
 * Whether a review was withdrawn by a refinement (rather than rejected by the user).
 * @param {string|null|undefined} reviewId - Review id.
 * @returns {boolean} True for refinement withdrawals.
 */
export const wasWithdrawnForRefinement = (reviewId) => Boolean(reviewId && withdrawnReviews.has(reviewId));
